import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { HttpClient } from 'src/core/common/domain/http/http-client';
import { ApiService } from 'src/core/api/domain/api.service';
import { ExceptionManagerService } from 'src/core/common/domain/exceptions/exception-manager.service';
import { ApiAccessService } from 'src/core/common/domain/api-access/api-access.service';
import { ConnectivityService } from 'src/core/connection/domain/connectivity.service';
import { NoConnectionException } from 'src/core/common/domain/exceptions/no-connection-exception';
import { TimeoutException } from 'src/core/common/domain/exceptions/timeout-exception';
import { Notification } from 'src/core/notifications/domain/notification';
import { NotificationRepository, NotificationsPage } from 'src/core/notifications/domain/notification-repository';
import { ApiNotificationsListResponse, ApiUnreadCountResponse } from './api-notifications-list-response';

@Injectable({
  providedIn: 'any',
})
export class HttpNotificationRepository extends NotificationRepository {
  // The API may not publish the notifications endpoints in every environment. Their absence is not
  // an error the user can act on, so the feature degrades to "nothing to show" instead of surfacing
  // an error. Checked on the raw response because the exception manager turns any 404 into
  // AmbitNotFoundException, which is indistinguishable from a real one.
  private static readonly NOT_PUBLISHED_STATUSES: Array<number> = [
    HttpStatusCode.NotFound,
    HttpStatusCode.MethodNotAllowed,
    HttpStatusCode.NotImplemented,
  ];
  // Marking a notification as read happens while the user is already navigating away, so it
  // waits far less than the default request timeout before giving up.
  private static readonly MARK_READ_TIMEOUT_IN_MILLISECONDS = 15000;
  // Angular reports a request that never got a response (no network, host unreachable, DNS
  // failure, TLS handshake refused) with status 0.
  private static readonly NO_RESPONSE_STATUS = 0;

  constructor(
    private readonly apiAccessService: ApiAccessService,
    private readonly apiService: ApiService,
    private readonly client: HttpClient,
    private readonly connectivityService: ConnectivityService,
    private readonly exceptionManager: ExceptionManagerService
  ) {
    super();
  }

  async list(page: number, pageSize: number): Promise<NotificationsPage> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const parameters = { page: String(page), page_size: String(pageSize) };
      const queryString = new URLSearchParams(parameters).toString();
      const url = `${this.apiService.baseUrl()}/notifications.list?${queryString}`;
      const response = await this.client.get<ApiNotificationsListResponse>(url, {}, {}, true, true);

      const notifications = response.notifications.map((notification) => Notification.fromPrimitives(notification));
      return {
        notifications,
        page: response.page,
        pageSize: response.page_size,
        totalItems: response.total_items,
        totalPages: response.total_pages,
      };
    } catch (exception) {
      if (this.endpointIsNotPublished(exception)) {
        this.warnEndpointIsNotPublished('notifications.list');
        return this.emptyPage(page, pageSize);
      }
      throw this.asDomainException(exception);
    }
  }

  async markRead(id: string): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      await this.failIfItTakesTooLong(
        this.client.post(`${this.apiService.baseUrl()}/notifications.read`, { id }, {}, true, true)
      );
    } catch (exception) {
      if (this.endpointIsNotPublished(exception)) {
        this.warnEndpointIsNotPublished('notifications.read');
        return;
      }
      throw this.asDomainException(exception);
    }
  }

  async markAllRead(): Promise<void> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      await this.client.post(`${this.apiService.baseUrl()}/notifications.read-all`, {}, {}, true, true);
    } catch (exception) {
      if (this.endpointIsNotPublished(exception)) {
        this.warnEndpointIsNotPublished('notifications.read-all');
        return;
      }
      throw this.asDomainException(exception);
    }
  }

  async unreadCount(): Promise<number> {
    try {
      if (!this.connectivityService.isOnline()) {
        throw new NoConnectionException();
      }

      await this.apiAccessService.renewApiAccessIfNeeded();
      const url = `${this.apiService.baseUrl()}/notifications.unread-count`;
      const response = await this.client.get<ApiUnreadCountResponse>(url, {}, {}, true, true);
      return response.unread_count;
    } catch (exception) {
      if (this.endpointIsNotPublished(exception)) {
        this.warnEndpointIsNotPublished('notifications.unread-count');
        return 0;
      }
      throw this.asDomainException(exception);
    }
  }

  // The request itself cannot be cancelled from here, so it may still reach the server after the
  // wait is given up on. That is harmless: marking an already read notification as read again is a
  // no-op for the API.
  private async failIfItTakesTooLong<ResponseType>(request: Promise<ResponseType>): Promise<ResponseType> {
    let timeoutId: ReturnType<typeof setTimeout>;
    const expiration = new Promise<never>((_resolve, reject) => {
      timeoutId = setTimeout(
        () => reject(new TimeoutException()),
        HttpNotificationRepository.MARK_READ_TIMEOUT_IN_MILLISECONDS
      );
    });

    try {
      return await Promise.race([request, expiration]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // A request that got no response is a connection problem, not a server error. It is mapped here
  // and not in ExceptionManagerService because doing it there would also change how task actions
  // react to a dropped request, and that behaviour is agreed with the client.
  private asDomainException(exception: any): Error {
    if (this.requestGotNoResponse(exception)) {
      return NoConnectionException.fromError(exception);
    }

    return this.exceptionManager.manage(exception);
  }

  private requestGotNoResponse(exception: unknown): exception is HttpErrorResponse {
    return exception instanceof HttpErrorResponse && exception.status === HttpNotificationRepository.NO_RESPONSE_STATUS;
  }

  private endpointIsNotPublished(exception: unknown): boolean {
    return (
      exception instanceof HttpErrorResponse &&
      HttpNotificationRepository.NOT_PUBLISHED_STATUSES.includes(exception.status)
    );
  }

  private warnEndpointIsNotPublished(endpoint: string): void {
    console.warn(`[HttpNotificationRepository] ${endpoint} is not published by the API`);
  }

  private emptyPage(page: number, pageSize: number): NotificationsPage {
    return {
      notifications: [],
      page,
      pageSize,
      totalItems: 0,
      totalPages: 0,
    };
  }
}
